import { memo, useContext, useEffect, useRef, useState } from "react";
import { motion, useMotionValue } from "framer-motion";
import { appendPath, getPointedData, prettierTitle, sortData } from "../utils";
import { cn } from "../../../utils";
import { useData, useSchema } from "../../../hooks/useStore";
import { renderData } from "../UiRenderer";
import { createPortal } from "react-dom";
import { ViewContext } from "../View";
import { ChevronRightCircle, Minimize2, Pin } from "lucide-react";
import isEqual from "react-fast-compare";
import { FloatingWindowRenderer } from "./Database";

const getPrimaryKeys = (pointerSchema, dataSchema) => {
  if (pointerSchema.thumbnail) return pointerSchema.thumbnail;

  return Object.keys(dataSchema).filter(
    (key) => dataSchema[key].function === "publicIdentifier"
  );
};

export const PointerRenderer = memo(
  ({
    infoObjectName,
    path,
    name,
    data: pointerId,
    schema: pointerSchema,
    options,
    is,
  }) => {
    const { rendered } = useContext(ViewContext);
    const allData = useData();

    // const pointerEntity = pointerSchema.type.replace(/__/g, "");
    const dataSchema = useSchema(infoObjectName, path);
    const primaryKeys = getPrimaryKeys(pointerSchema, dataSchema);

    const data = getPointedData(allData, pointerSchema, pointerId);

    const [expanded] = useState(options.hideKeys && !is.previewView);
    const [pop, setPop] = useState(false);
    const rootObjRef = useRef(null);

    const handleClick = (e) => {
      if (expanded) return;

      e.stopPropagation();
      setPop(!pop);
    };

    return (
      <motion.div
        key={`${infoObjectName}-${path}`}
        ref={rootObjRef}
        className={cn(
          "r-pointer-container",
          expanded && "r-expanded",
          rendered && "r-rendered r-rendered-container",
          options.hideKeys && "r-minimal"
        )}
        initial={false}
        animate={{ opacity: 1 }}
      >
        {!!name && !options.hideKeys && (
          <motion.span
            className={cn("r-pointer-key r-key", rendered && "r-rendered")}
          >
            {prettierTitle(name)}
          </motion.span>
        )}

        <div className="r-pointer-content" onClick={handleClick}>
          <div
            className={cn(
              expanded && "r-pointer-content-expanded",
              !expanded && "r-pointer-content-unexpanded"
            )}
          >
            {(() => {
              let isThumbnailMainVal = true;

              let d = sortData(data);

              if (!expanded) {
                d = Object.fromEntries(
                  primaryKeys
                    .filter((key) => key in d)
                    .map((key) => [key, d[key]])
                );
              }

              const smallerThumbnails = [];

              return (
                <>
                  {Object.entries(d).map(([key, value]) => {
                    if (!expanded && !primaryKeys.includes(key)) return null;
                    if (key === "id" || key.includes("_id")) return null;

                    const r = () => {
                      isThumbnailMainVal = false;

                      return renderData(
                        infoObjectName,
                        appendPath(path, key),
                        key,
                        value,
                        dataSchema[key],
                        { hideKeys: !expanded, isPreviewValue: !expanded }
                      );
                    };

                    if (!isThumbnailMainVal && !expanded) {
                      smallerThumbnails.push(r());
                      return null;
                    }

                    return r();
                  })}
                  {!expanded && smallerThumbnails.length > 0 && (
                    <div className="r-pointer-smaller-thumbnail">
                      {smallerThumbnails}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
          {!expanded && (
            <ChevronRightCircle
              size={12}
              color="#bbb"
              style={{
                transform: "translateY(0.5px)", // make visually vertically centered
              }}
            />
          )}
        </div>

        {!expanded &&
          createPortal(
            <ExpandedPointer
              rootObjRef={rootObjRef}
              infoObjectName={infoObjectName}
              path={path}
              data={data}
              name={name}
              dataSchema={dataSchema}
              primaryKeys={primaryKeys}
              pop={pop}
              setPop={setPop}
            />,
            document.body.querySelector("#root")
          )}
      </motion.div>
    );
  },
  isEqual
);

const ExpandedPointer = ({
  rootObjRef,
  infoObjectName,
  path,
  data,
  name,
  dataSchema,
  primaryKeys,
  pop,
  setPop,
}) => {
  const { rendered } = useContext(ViewContext);
  const [pinned, setPinned] = useState(false);

  const rootPos = rootObjRef.current?.getBoundingClientRect();
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const padding = rendered ? 1 : 0.5;
  const paddingRem = `${padding}rem`;

  useEffect(() => {
    if (rootObjRef.current) {
      const rect = rootObjRef.current.getBoundingClientRect();
      x.set(rect.left + window.scrollX - padding * 16);
      y.set(rect.top + window.scrollY - padding * 16);
    }
  }, [padding, rootObjRef, rootPos, x, y, pop]);

  return (
    <>
      {pop && (
        <motion.div
          className={cn("r-pop-pointer", rendered && "r-rendered")}
          initial={{
            opacity: 0,
            paddingTop: paddingRem,
            paddingBottom: 0,
            paddingLeft: paddingRem,
            paddingRight: 0,
          }}
          animate={{
            opacity: 1,
            paddingTop: paddingRem,
            paddingBottom: paddingRem,
            paddingLeft: paddingRem,
            paddingRight: paddingRem,
          }}
          exit={{
            opacity: 0,
            paddingTop: paddingRem,
            paddingBottom: 0,
            paddingLeft: paddingRem,
            paddingRight: 0,
          }}
          style={{
            top: y,
            left: x,
          }}
        >
          <div className="r-pointer-header">
            <div className="r-icons">
              <button
                className="r-pop-pointer-close r-icon r-icon-clickable r-icon-warning"
                onClick={() => setPop(false)}
              >
                <Minimize2 size={12} />
              </button>
              <button
                className="r-pop-pointer-close r-icon r-icon-clickable r-icon-warning"
                onClick={() => {
                  setPop(false);
                  setPinned(!pinned);
                }}
              >
                <Pin size={12} />
              </button>
            </div>
            <span className="r-pointer-key r-key">{prettierTitle(name)}</span>
          </div>
          {Object.entries(data).map(([key, value], index) => {
            if (key === "id" || key.includes("_id")) return null;
            const isPrimaryKey = primaryKeys && primaryKeys[0] === key;

            return (
              <motion.div
                key={key}
                initial={isPrimaryKey ? {} : { opacity: 0, y: -10 }}
                animate={isPrimaryKey ? {} : { opacity: 1, y: 0 }}
                transition={{
                  delay: Math.max(0, (index - 2) * 0.03),
                }}
                className="r-pointer-content-wrapper"
              >
                {renderData(
                  infoObjectName,
                  appendPath(path, key),
                  key,
                  value,
                  dataSchema[key],
                  { hideKeys: false }
                )}
              </motion.div>
            );
          })}
        </motion.div>
      )}
      {pinned &&
        createPortal(
          <FloatingWindowRenderer
            infoObjectName={infoObjectName}
            path={path}
            name={name}
            data={data}
            setUnfolded={setPinned}
            buttonRef={rootObjRef}
            setTargeted={() => {}}
          />,
          document.body.querySelector("#root")
        )}
    </>
  );
};
